import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { recordMetric, isRumEnabled } from '@/lib/rum';

/**
 * Hook para medir tempo de navegação entre rotas
 * 
 * Mede: tempo desde que usuário clicou até rota estar "pronta"
 * Sampling: 100% (poucos eventos)
 */
export function useRoutePerformance(): void {
  const location = useLocation();
  const routeStartRef = useRef<number>(performance.now());
  const lastRouteRef = useRef<string>('');
  const isFirstMount = useRef(true);

  useEffect(() => {
    // Skip se RUM desabilitado
    if (!isRumEnabled()) return;
    
    const currentRoute = location.pathname;
    const now = performance.now();
    
    // Pular primeiro mount (não é navegação real)
    if (isFirstMount.current) {
      isFirstMount.current = false;
      routeStartRef.current = now;
      lastRouteRef.current = currentRoute;
      return;
    }
    
    // Se mudou de rota, registrar timing da navegação
    if (lastRouteRef.current && lastRouteRef.current !== currentRoute) {
      const duration = now - routeStartRef.current;
      
      // Só registrar navegações reais (> 10ms) e válidas (< 30s)
      if (duration > 10 && duration < 30000) {
        recordMetric({
          type: 'route_timing',
          name: 'navigation',
          value: duration,
          context: {
            from: lastRouteRef.current,
            to: currentRoute,
          },
        });
      }
    }
    
    // Iniciar timing da nova rota
    routeStartRef.current = now;
    lastRouteRef.current = currentRoute;
  }, [location.pathname]);
}
